// @ts-nocheck
import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { MenuItem } from '@blueprintjs/core';
import { Suggest } from '@blueprintjs/select';
import { debounce } from 'lodash';
import { useFormikContext } from 'formik';

import { FormattedMessage as T } from '@/components';
import classNames from 'classnames';
import { CLASSES } from '@/constants/classes';
import intl from 'react-intl-universal';
import { useSearchableContacts } from '@/hooks/query';
import { createNewItemFromQuery, createNewItemRenderer } from './utils';
import withDrawerActions from '@/containers/Drawer/withDrawerActions';
import { useCreateAutofillListener } from '@/hooks/state/autofill';
import { DRAWERS } from '@/constants/drawers';

function CustomersSearchableSuggestFieldRoot({
  // #withDrawerActions
  openDrawer,

  // #ownProps
  initialCustomerId,
  selectedCustomerId,
  defaultTextSelect = intl.get('select_customer_account'),
  onCustomerSelected,
  allowCreate = false,
  name,
  popoverFill = false,

  ...suggestProps
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  // Debounce the search query
  const debouncedSetSearch = useRef(
    debounce((query) => {
      setDebouncedSearchQuery(query);
    }, 300)
  );

  useEffect(() => {
    debouncedSetSearch.current(searchQuery);
  }, [searchQuery]);

  // Fetch contacts based on search query
  const { data: contactsList = [], isLoading } = useSearchableContacts(
    debouncedSearchQuery,
    {
      keepPreviousData: true,
    }
  );

  // Filter contacts to only show customers
  const customers = useMemo(
    () =>
      contactsList
        .filter((contact) => contact.contact_service === 'customer')
        .map((customer) => ({
          ...customer,
          _id: `${customer.id}_${customer.contact_service}`,
        })),
    [contactsList],
  );

  const { setFieldValue } = useFormikContext();

  // Maybe inject create new item props to suggest component.
  const maybeCreateNewItemRenderer = allowCreate ? createNewItemRenderer : null;
  const maybeCreateNewItemFromQuery = allowCreate
    ? createNewItemFromQuery
    : null;

  // Creates autofill listener once the quick customer drawer submit the form.
  const autofillRef = useCreateAutofillListener((payload: any) => {
    if (name) {
      setFieldValue(name, payload.customerId);
    }
  });

  const initialCustomer = useMemo(
    () => customers.find((a) => a.id === initialCustomerId),
    [initialCustomerId, customers],
  );

  const [selectedCustomer, setSelectedCustomer] = useState(
    initialCustomer || null,
  );

  useEffect(() => {
    if (typeof selectedCustomerId !== 'undefined') {
      const customer = selectedCustomerId
        ? customers.find((a) => a.id === selectedCustomerId)
        : null;
      setSelectedCustomer(customer);
    }
  }, [selectedCustomerId, customers, setSelectedCustomer]);

  const customerRenderer = useCallback(
    (customer, { handleClick }) => (
      <MenuItem
        key={customer.id}
        text={customer.display_name}
        label={customer.formatted_balance}
        onClick={handleClick}
      />
    ),
    [],
  );

  const onCustomerSelect = useCallback(
    (customer) => {
      setSelectedCustomer({ ...customer });
      onCustomerSelected && onCustomerSelected(customer);
    },
    [setSelectedCustomer, onCustomerSelected],
  );

  const handleInputValueRenderer = (inputValue) => {
    if (inputValue) {
      return inputValue.display_name.toString();
    }
  };

  // Handle query change for server-side search
  const handleQueryChange = useCallback((query) => {
    setSearchQuery(query);
  }, []);

  // Client-side filter is minimal since we're doing server-side search
  const filterCustomers = useCallback(() => {
    // Return true for all items since filtering is done server-side
    return true;
  }, []);

  // Handles the create item click.
  const handleCreateItemClick = (item) => {
    const displayName = item.name;
    openDrawer(DRAWERS.QUICK_CREATE_CUSTOMER, { autofillRef, displayName });
  };

  return (
    <Suggest
      items={customers}
      noResults={
        <MenuItem 
          disabled={true} 
          text={isLoading ? <T id={'loading'} /> : <T id={'no_results'} />} 
        />
      }
      itemRenderer={customerRenderer}
      itemPredicate={filterCustomers}
      onItemSelect={onCustomerSelect}
      selectedItem={selectedCustomer}
      inputProps={{ placeholder: defaultTextSelect }}
      resetOnClose={true}
      popoverProps={{ minimal: true, boundary: 'window' }}
      inputValueRenderer={handleInputValueRenderer}
      onQueryChange={handleQueryChange}
      query={searchQuery}
      createNewItemRenderer={maybeCreateNewItemRenderer}
      createNewItemFromQuery={maybeCreateNewItemFromQuery}
      onCreateItemSelect={allowCreate ? handleCreateItemClick : undefined}
      className={classNames(CLASSES.FORM_GROUP_LIST_SELECT, {
        [CLASSES.SELECT_LIST_FILL_POPOVER]: popoverFill,
      })}
      {...suggestProps}
    />
  );
}

export const CustomersSearchableSuggestField = withDrawerActions(CustomersSearchableSuggestFieldRoot);
